import { useMemo } from 'react';

import { styled } from '@mui/material';
import capitalize from 'lodash/capitalize';

import {
  Divider,
  Skeleton,
  InfoTooltip,
  Box,
  Stack,
} from '@cast/design-system';
import { ComputeResourceType } from '@cast/shared-types';
import { Node } from '@cast/types';

import { NodeStatusChip, ResourceOfferingChip } from 'components/chips';
import { Detail } from 'components/drawer';
import { LoadingProvider } from 'core/loading';
import { ViewNodeAuditLogsLink } from 'features/cluster/nodes/_components/ViewNodeAuditLogsLink';
import { useNode } from 'features/cluster/nodes/hooks/useNode';
import { NodeWithDependencies } from 'features/cluster/nodes/types';
import { useNodeStorageQuery } from 'hooks/queries/nodes';
import { notReachable } from 'utils/types/not-reachable';

import { ConditionalDetail } from './_components/ConditionalDetail';
import { ExpandableDrawerSection } from './_components/ExpandableDrawerSection';
import { NodeRegionDetail } from './_components/NodeRegionDetail';
import { NodeProblemsAlert } from '../../../_components/NodeProblemsAlert';
import { transformNodeRole } from '../../../_utils/common';
import { getNodeSpecs } from '../../../_utils/getNodeSpecs';
import { EphemeralStorageMetricsSection } from '../../../components/EphemeralStorageMetricsSection';
import { NodeCostSection } from '../../../components/NodeCostSection';
import { NodeDetailsTime } from '../../../components/NodeDetailsTime';
import { NodeDetailsWithTooltip } from '../../../components/NodeDetailsWithTooltip';
import { NodeMetricsSection } from '../../../components/NodeMetricsSection';
import { NodeRemovalSection } from '../../../components/NodeRemovalSection';
import { NodeTemplatesSection } from '../../../components/NodeTemplatesSection';
import { getNodeMetrics } from '../../../utils/getNodeMetrics';

import { useCluster } from '@cast/shared-cluster-context-data-access';

const BleedingBox = styled(Box)({
  marginInline: '-32px',
});

const WORKLOADS_REFETCH_INTERVAL_MS = 30_000;

type Props = {
  clusterId: string;
  node: Node;
};

type NodeDetails =
  | {
      type: 'node';
      node: Node;
    }
  | {
      type: 'node-with-dependencies';
      node?: NodeWithDependencies;
    };

export const NodeDetailsDrawerContent = ({ clusterId, node }: Props) => {
  const { isCastAnywhere } = useCluster();

  const { node: nodeWithDependencies, isLoading } = useNode({
    clusterId,
    nodeId: node.id,
    withPricing: true,
    withIssues: true,
    withRebalancingWorkloads: true,
    workloadsRefetchInterval: WORKLOADS_REFETCH_INTERVAL_MS,
    enabled: !isCastAnywhere,
  });

  const { data: storageData } = useNodeStorageQuery({
    clusterId,
  });

  const nodeDetails: NodeDetails = isCastAnywhere
    ? { type: 'node', node }
    : { type: 'node-with-dependencies', node: nodeWithDependencies };

  const hasNodeDetails = !isLoading;

  const storageMetrics = useMemo(() => {
    return storageData?.nodes?.find((n) => n.nodeName === node.name);
  }, [storageData, node.name]);

  const { cpuMetrics, memoryMetrics, ephemeralStorageMetrics } = getNodeMetrics(
    nodeDetails.node,
    storageMetrics
  );
  const specs = nodeDetails.node ? getNodeSpecs(nodeDetails.node) : '-';

  const hasNodeInstanceDetails =
    nodeDetails.node?.instanceName ||
    nodeDetails.node?.instanceId ||
    nodeDetails.node?.castNodeId;

  return (
    <LoadingProvider isLoading={isLoading}>
      <Stack mt={4} mb={24}>
        <Stack gap={24} mb={24}>
          {hasNodeDetails ? (
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <NodeStatusChip
                testId="node-status-chip"
                status={nodeDetails.node?.state.phase}
                size="large"
              />
              <ViewNodeAuditLogsLink node={node} />
            </Box>
          ) : (
            <Skeleton width="62px" height="28px" />
          )}

          {(() => {
            switch (nodeDetails.type) {
              case 'node':
                return null;
              case 'node-with-dependencies':
                return nodeDetails.node ? (
                  <NodeProblemsAlert node={nodeDetails.node} />
                ) : null;
              default:
                return notReachable(nodeDetails);
            }
          })()}

          <BleedingBox>
            <Divider />
          </BleedingBox>
          <Stack gap={8}>
            <Detail
              title="Joined:"
              testId="node-details-joined-date"
              detail={
                <NodeDetailsTime
                  date={nodeDetails.node?.joinedAt}
                  preciseFormat={false}
                />
              }
              sx={{ alignItems: 'flex-start' }}
            />
            <Detail
              title="Created:"
              testId="node-details-created-date"
              detail={
                <NodeDetailsTime
                  date={nodeDetails.node?.createdAt}
                  preciseFormat={false}
                />
              }
              sx={{ alignItems: 'flex-start' }}
            />

            <ConditionalDetail
              title="Added by:"
              testId="node-details-added-by"
              detail={nodeDetails.node?.addedBy}
              sx={{ alignItems: 'flex-start' }}
            />
          </Stack>
        </Stack>
        <BleedingBox>
          <Divider />
          <ExpandableDrawerSection
            title="Overview"
            testId="node-details-overview-section"
          >
            <Detail
              title="Id:"
              testId="node-details-id"
              detail={<NodeDetailsWithTooltip value={nodeDetails.node?.id} />}
            />
            <Detail
              title="Role:"
              testId="node-details-role"
              detail={
                nodeDetails.node?.role
                  ? capitalize(transformNodeRole(nodeDetails.node.role))
                  : undefined
              }
            />
            <ConditionalDetail
              title="Instance type:"
              detail={nodeDetails.node?.instanceType}
              testId="node-details-instance-type"
            />
            <Detail title="Spec:" testId="node-details-spec" detail={specs} />
            <Detail
              title={
                <>
                  Resource offering
                  <InfoTooltip
                    title="Resource offering is determined by node labels rather than real-time cloud provider data."
                    noDefaultIconStyling={false}
                  />
                  :
                </>
              }
              testId="node-details-resource-offering"
              detail={
                nodeDetails.node ? (
                  <Box display="flex" alignItems="center" component="span">
                    <ResourceOfferingChip
                      resourceOffering={nodeDetails.node.resourceOffering}
                    />
                  </Box>
                ) : undefined
              }
            />
            <Detail
              title="Operating system:"
              testId="node-details-operating-system"
              detail={
                nodeDetails.node?.nodeInfo?.operatingSystem
                  ? capitalize(nodeDetails.node?.nodeInfo?.operatingSystem)
                  : undefined
              }
            />
            <Detail
              title="Operating system image:"
              testId="node-details-operating-system-image"
              detail={
                <NodeDetailsWithTooltip
                  value={nodeDetails.node?.nodeInfo?.osImage}
                  showCopy={false}
                />
              }
            />
            <Detail
              title="Architecture:"
              testId="node-details-architecture"
              detail={nodeDetails.node?.nodeInfo?.architecture}
            />
          </ExpandableDrawerSection>
        </BleedingBox>
        <BleedingBox>
          <Divider />
          <ExpandableDrawerSection
            title="Cloud details"
            testId="cloud-details-section"
          >
            <Stack gap={12} divider={<Divider />}>
              <Stack gap={8}>
                <Detail
                  title="Cloud:"
                  detail={nodeDetails.node?.cloud?.toUpperCase()}
                  testId="node-details-cloud"
                />
                <Detail
                  title="Availability zone:"
                  detail={nodeDetails.node?.zone}
                  showCopy
                  testId="node-details-availability-zone"
                />

                <NodeRegionDetail nodeDetails={nodeDetails.node} />

                <Detail
                  title="IP address:"
                  detail={nodeDetails.node?.network?.publicIp || '-'}
                  showCopy
                  testId="node-details-ip-address"
                />
              </Stack>
              <NodeCostSection node={nodeDetails.node} />
            </Stack>
          </ExpandableDrawerSection>
        </BleedingBox>

        <BleedingBox>
          <Divider />
          <ExpandableDrawerSection
            title="Resources utilization"
            testId="resources-utilization-section"
          >
            <Stack gap={14}>
              <NodeMetricsSection
                type={ComputeResourceType.CPU}
                metrics={cpuMetrics}
                testId="node-details-cpu-metrics"
              />
              <NodeMetricsSection
                type={ComputeResourceType.MEMORY}
                metrics={memoryMetrics}
                testId="node-details-memory-metrics"
              />
              {ephemeralStorageMetrics && (
                <EphemeralStorageMetricsSection
                  metrics={ephemeralStorageMetrics}
                  testId="node-details-ephemeral-storage-metrics"
                />
              )}
            </Stack>
          </ExpandableDrawerSection>
        </BleedingBox>

        <BleedingBox>
          <Divider />
          <ExpandableDrawerSection
            title="Node information"
            testId="node-information-section"
          >
            <Stack gap={14} divider={<Divider />}>
              <NodeRemovalSection node={nodeDetails.node} />
              {hasNodeInstanceDetails && (
                <Stack gap={8}>
                  <Detail
                    title="Cloud node name:"
                    testId="node-details-cloud-node-name"
                    detail={
                      <NodeDetailsWithTooltip
                        value={nodeDetails.node?.instanceName}
                      />
                    }
                  />
                  <Detail
                    title="Cloud node ID:"
                    testId="node-details-cloud-node-id"
                    detail={
                      <NodeDetailsWithTooltip
                        value={nodeDetails.node?.instanceId}
                      />
                    }
                  />
                  <Detail
                    title="CAST node ID:"
                    testId="node-details-cast-node-id"
                    detail={
                      <NodeDetailsWithTooltip
                        value={nodeDetails.node?.castNodeId}
                      />
                    }
                  />
                </Stack>
              )}
              <Stack gap={8}>
                <Detail
                  title="Kernel:"
                  testId="node-details-kernel"
                  detail={nodeDetails.node?.nodeInfo?.kernelVersion}
                  showCopy
                />
                <Detail
                  title="Kube proxy:"
                  testId="node-details-kube-proxy"
                  detail={nodeDetails.node?.nodeInfo?.kubeProxyVersion}
                  showCopy
                />
                <Detail
                  title="Kubelet:"
                  testId="node-details-kubelet"
                  detail={nodeDetails.node?.nodeInfo?.kubeletVersion}
                  showCopy
                />
              </Stack>
              <NodeTemplatesSection
                node={nodeDetails.node}
                clusterId={clusterId}
                showDivider={false}
                withVersionPrefix={true}
              />
            </Stack>
          </ExpandableDrawerSection>
        </BleedingBox>
      </Stack>
    </LoadingProvider>
  );
};
